import { Button, Dialog, DialogContent, DialogDescription, DialogTitle } from '@kalos/ui';
import { GroupIcon } from '@radix-ui/react-icons';
import { createFileRoute } from '@tanstack/react-router';
import { Settings2 } from 'lucide-react';
import { useState } from 'react';

import { useAuth } from '../../context/AuthContext';
import { useGetInternalLinkQuery } from '../../hooks/react-query/useInternalLinkQuery';
import { AdvancedSearch } from '../../modules/ComponentsLibrary/AdvancedSearch';
import { type Kind } from '../../modules/ComponentsLibrary/AdvancedSearch/constants';
import { employeesSearchScheme } from '../../modules/ComponentsLibrary/AdvancedSearch/EmployeesSearch/utils';
import { ManageOrganization } from '../../modules/ComponentsLibrary/OrgStructure';
import { orgChartAdminCheck } from '../../tools/permissionChecks';

export const Route = createFileRoute('/employees/')({
  component: RouteComponent,
  validateSearch: employeesSearchScheme,
  head(ctx) {
    return {
      meta: [{ title: 'Employee Directory' }],
    };
  },
});

const onlyEmployeesKind: Kind[] = ['employees'];
function RouteComponent() {
  const { data: orgChartLink } = useGetInternalLinkQuery({ id: 31 });
  const [manageOpen, setManageOpen] = useState(false);

  /*
    Privilege 116. Teams, job titles and levels are its admins' to define;
    everyone else consumes them through the pickers on the employee record.

    Not behind org-chart.module, for the same reason the chart page is not: this
    is where the structure the chart draws gets maintained, and a flag that
    takes it away leaves its admins with a chart they cannot fill. It is already
    invisible to everyone else, so the flag has little left to hide here.
  */
  const isOrgChartAdmin = orgChartAdminCheck(useAuth().user);

  return (
    <>
      <AdvancedSearch
        title="Employee Directory"
        kinds={onlyEmployeesKind}
        printableEmployees
        headerActions={
          <>
            {/*
              Manage Organization sits beside the chart because the two are the
              same subject from opposite ends: the chart is the structure drawn,
              this is the structure defined.
            */}
            {isOrgChartAdmin ? (
              <Button
                variant="outline"
                size="sm"
                className="flex items-center gap-2"
                onClick={() => setManageOpen(true)}
              >
                <Settings2 className="size-4" />
                <span>Manage Organization</span>
              </Button>
            ) : null}
            {/*
              Opens internal link 31, the hand-maintained chart that
              /reports/employee-org-chart replaces (ClickUp 86bcfugn3). This
              becomes a navigation to that route once the chart is readable
              beyond OrgChartAdmin; until then it would send most of this page's
              readers to a screen that turns them away.
            */}
            <Button
              variant="outline"
              size="sm"
              className="flex items-center gap-2"
              onClick={() => window.open(orgChartLink?.url ?? '', '_blank')}
            >
              <GroupIcon className="size-4" />
              <span>Organization Chart</span>
            </Button>
          </>
        }
      />

      {isOrgChartAdmin ? (
        <Dialog open={manageOpen} onOpenChange={setManageOpen}>
          <DialogContent className="flex max-h-[85vh] max-w-5xl flex-col overflow-auto">
            <DialogTitle>Manage Organization</DialogTitle>
            <DialogDescription>
              The teams, job titles and levels used across employee profiles and the organization
              chart.
            </DialogDescription>
            <div className="pt-2">
              <ManageOrganization onNavigateAway={() => setManageOpen(false)} />
            </div>
          </DialogContent>
        </Dialog>
      ) : null}
    </>
  );
}
